import React, {useCallback, useState} from 'react';
import {
  AccessibilityRole,
  GestureResponderEvent,
  Platform,
  Text,
  TextStyle,
  TouchableWithoutFeedback,
  View,
  ViewProps,
  StyleSheet,
  CursorValue,
} from 'react-native';
import {openExternalLink, openExternalPhone} from '../../pipes';
import {downloadFileToDisk} from '../../pipes/fileHelpers';
import {useSelector} from 'react-redux';
import {useAppDispatch} from '../../../redux';
import {profileTokenSelector} from '../../../redux/selectors/profile';
import {addNotification} from '../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../snackbar/model';
import FileViewer from 'react-native-file-viewer';
import {sleep} from '../../pipes';
import {useIsDesktop} from '../../hooks/useIsDesktop';

interface ScreenParams {
  [key: string]: string | number | ScreenParams;
}

interface LinkProps {
  href?: string;
  testID: string;
  navigate?: (screen: string, params: ScreenParams) => void;
  screen?: string;
  style?: ViewProps['style'];
  textStyle?: TextStyle;
  external?: boolean;
  download?: boolean;
  fileName?: string;
  accessibilityRole?: AccessibilityRole;
  accessibilityLabel?: string;
  screenParams?: ScreenParams;
  dataSetClass?: string;
  children?: React.ReactNode;
}

export const Link: React.FC<LinkProps> = ({
  external,
  screen,
  href,
  testID,
  navigate,
  style,
  textStyle,
  children,
  screenParams,
  download,
  fileName,
  accessibilityRole = 'link',
  accessibilityLabel = '',
  dataSetClass,
}) => {
  const dispatch = useAppDispatch();
  const isDesktop = useIsDesktop();
  const token = useSelector(profileTokenSelector());
  const [fileLoading, setFileLoading] = useState(false);
  const pressHandler = useCallback(
    async (event: GestureResponderEvent) => {
      if (screen && navigate) {
        const params = {
          ...screenParams,
        } as ScreenParams;
        if (href) {
          params.url = href;
        }
        event.preventDefault();
        navigate(screen, params);
      } else if (external && href) {
        event.preventDefault();
        href.startsWith('http', 0) ? await openExternalLink(href) : await openExternalPhone(href);
      } else if (href && download) {
        const downloadedFileName = fileName || Date.now().toString();
        try {
          setFileLoading(true);
          dispatch(
            addNotification({
              message: `Началась загрузка файла "${fileName}"`,
              type: SnackbarNotificationType.Info,
            }),
          );
          await sleep(200);

          const result = await downloadFileToDisk(href, downloadedFileName, token || undefined);
          if (Platform.OS !== 'web' && FileViewer) {
            await FileViewer.open(result, {showOpenWithDialog: true});
          }
        } catch (e: any) {
          dispatch(
            addNotification({
              message: e?.message ?? 'Ошибка при загрузке файла',
              type: SnackbarNotificationType.Error,
            }),
          );
        } finally {
          setFileLoading(false);
        }
      }
    },
    [screen, href],
  );

  const externalHrefObj = !download ? {href} : {};

  const dataSet = {
    dataSet: {
      class: dataSetClass ?? 'link',
    },
  };

  const localStyle = StyleSheet.flatten([
    style,
    {
      opacity: fileLoading ? 0.36 : 1,
      ...(isDesktop
        ? {
            cursor: 'pointer' as CursorValue,
          }
        : {}),
    },
  ]);

  return (
    <TouchableWithoutFeedback testID={testID} onPress={pressHandler} disabled={fileLoading}>
      <View
        accessibilityRole={accessibilityRole}
        accessibilityLabel={accessibilityLabel}
        style={localStyle}
        {...externalHrefObj}
        {...dataSet}>
        {typeof children === 'string' ? <Text style={textStyle}>{children}</Text> : children}
      </View>
    </TouchableWithoutFeedback>
  );
};
